import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { AccessTime as TimeIcon, AttachMoney as MoneyIcon, Label as CategoryIcon } from '@mui/icons-material';

import { useServices } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { formatCurrency, formatDuration } from '../utils/formatters';
import photoService from '../services/photoService';

import PageLayout from '../components/ui/PageLayout';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import AppSnackbar from '../components/ui/AppSnackbar';
import ServiceForm from '../components/forms/ServiceForm';
import ServiceAvatar from '../components/ServiceAvatar';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';

// ── Utilitário: iniciais do serviço (até 2 letras das primeiras palavras) ─────
function getServiceInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export default function ServicesPage() {
  const { items: services, loading, error, createAsync, updateAsync, reload } = useServices();
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const photoDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const [photoTimestampOverrides, setPhotoTimestampOverrides] = useState({});
  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState('name_asc');

  // ── Dados derivados com timestamp de foto ───────────────────────────────────
  const servicesWithPhoto = useMemo(() => {
    return (services || []).map(s => ({
      ...s,
      photoTimestamp:
        photoTimestampOverrides[s.id] ?? photoService.getServicePhotoTimestamp(s.id),
    }));
  }, [services, photoTimestampOverrides]);

  // ── Filtro e ordenação ──────────────────────────────────────────────────────
  const filteredServices = useMemo(() => {
    let result = [...servicesWithPhoto];
    const q = searchText.trim().toLowerCase();
    if (q) {
      result = result.filter(
        s =>
          (s.name || '').toLowerCase().includes(q) ||
          (s.displayName || '').toLowerCase().includes(q) ||
          (s.description || '').toLowerCase().includes(q) ||
          (s.category || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      if (sortOrder === 'name_desc')
        return (b.name || '').localeCompare(a.name || '', 'pt-BR');
      if (sortOrder === 'duration_asc')
        return (a.defaultDurationMinutes || 0) - (b.defaultDurationMinutes || 0);
      if (sortOrder === 'price_asc')
        return (a.defaultPrice || 0) - (b.defaultPrice || 0);
      // name_asc default
      return (a.name || '').localeCompare(b.name || '', 'pt-BR');
    });
    return result;
  }, [servicesWithPhoto, searchText, sortOrder]);

  // ── Handlers CRUD ──────────────────────────────────────────────────────────
  const handleCreate = async data => {
    try {
      await createAsync(data);
      formDialog.handleClose();
      showSuccess('Serviço criado com sucesso!');
    } catch (err) {
      showError('Erro ao criar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async data => {
    try {
      await updateAsync(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Serviço atualizado com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  // ── Handlers de foto ───────────────────────────────────────────────────────
  const handlePhotoClick = service => {
    photoDialog.handleOpen(service);
  };

  const handlePhotoUpload = async file => {
    const service = photoDialog.data;
    if (!service?.id) {
      showError('Serviço não selecionado');
      return;
    }
    try {
      await photoService.uploadServicePhoto(file, service.id);
      const timestamp = Date.now();
      photoService.saveServicePhotoTimestamp(service.id, timestamp);
      setPhotoTimestampOverrides(prev => ({ ...prev, [service.id]: timestamp }));
      photoDialog.handleClose();
      showSuccess('Foto atualizada com sucesso!');
      reload();
    } catch (err) {
      showError('Erro ao fazer upload: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteDone = useCallback(() => {
    deleteDialog.handleClose();
    showSuccess('Serviço excluído com sucesso!');
    reload();
  }, [deleteDialog, showSuccess, reload]);

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (loading) return <LoadingState message="Carregando serviços..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <PageLayout
      title="Serviços"
      subtitle="Catálogo de serviços oferecidos"
      actionLabel="Novo Serviço"
      onAction={() => formDialog.handleOpen(null)}
    >
      {/* ── Filtros ── */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Buscar por nome, descrição ou categoria..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Select
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="name_asc">Nome A → Z</MenuItem>
            <MenuItem value="name_desc">Nome Z → A</MenuItem>
            <MenuItem value="duration_asc">Menor duração</MenuItem>
            <MenuItem value="price_asc">Menor preço</MenuItem>
          </Select>
        </Box>
      </Paper>

      {/* ── Lista ── */}
      {filteredServices.length === 0 ? (
        <EmptyState
          message={
            searchText
              ? `Nenhum serviço encontrado para "${searchText}"`
              : 'Nenhum serviço cadastrado. Clique em "Novo Serviço" para começar.'
          }
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            '@media (min-width: 1700px)': {
              gridTemplateColumns: 'repeat(5, 1fr)',
            },
          }}
        >
          {filteredServices.map(service => (
            <ServiceCard
              key={service.id}
              service={service}
              onEdit={() => formDialog.handleOpen(service)}
              onDelete={() => deleteDialog.handleOpen(service)}
              onPhotoClick={() => handlePhotoClick(service)}
            />
          ))}
        </Box>
      )}

      {/* ── Dialogs ── */}
      <ServiceForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
      />

      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={handleDeleteDone}
        deletePath={deleteDialog.data ? `/services/${deleteDialog.data.id}` : ''}
        entityName={deleteDialog.data?.name}
      />

      <ProfilePhotoUpload
        open={photoDialog.open}
        onClose={photoDialog.handleClose}
        onUpload={handlePhotoUpload}
        title="Foto do Serviço"
        currentPhotoUrl={
          photoDialog.data
            ? photoService.getServicePhotoUrl(photoDialog.data.id)
            : null
        }
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}

// ── Card individual de serviço ─────────────────────────────────────────────
function ServiceCard({ service, onEdit, onDelete, onPhotoClick }) {
  const initials = getServiceInitials(service.name);
  const hasDisplayName = service.displayName && service.displayName !== service.name;
  const hasDuration = service.defaultDurationMinutes > 0;
  const hasPrice = service.defaultPrice > 0;
  const hasCategory = !!service.category;
  const hasDescription = !!service.description;

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 1.25,
        gap: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: 'secondary.main',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'secondary.light',
          borderLeftColor: 'secondary.main',
        },
      }}
    >
      {/* ── Header: avatar + nome + ações ── */}
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', minWidth: 0 }}>
        {/* Avatar clicável para upload de foto */}
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <ServiceAvatar
            serviceId={service.id}
            photoTimestamp={service.photoTimestamp}
            name={service.name}
            sx={{ width: 44, height: 44, fontSize: '1rem', bgcolor: 'secondary.main' }}
          >
            {initials}
          </ServiceAvatar>
          <Tooltip title="Alterar foto">
            <Box
              onClick={onPhotoClick}
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0)',
                cursor: 'pointer',
                transition: 'background 0.2s',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.45)' },
                '&:hover .camera-icon': { opacity: 1 },
              }}
            >
              <CameraIcon
                className="camera-icon"
                sx={{ color: 'white', fontSize: 16, opacity: 0 }}
              />
            </Box>
          </Tooltip>
        </Box>

        {/* Nome + nome público */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            noWrap
            sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}
          >
            {service.name}
          </Typography>
          {hasDisplayName && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {service.displayName}
            </Typography>
          )}
        </Box>

        {/* Ações */}
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0, ml: 'auto' }}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={onEdit} color="primary">
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={onDelete} color="error">
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Divisor ── */}
      {(hasDescription || hasDuration || hasPrice || hasCategory) && (
        <Divider sx={{ opacity: 0.5 }} />
      )}

      {/* ── Descrição ── */}
      {hasDescription && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {service.description}
        </Typography>
      )}

      {/* ── Chips: duração, preço, categoria ── */}
      {(hasDuration || hasPrice || hasCategory) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
          {hasDuration && (
            <Chip
              icon={<TimeIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={formatDuration(service.defaultDurationMinutes)}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
          {hasPrice && (
            <Chip
              icon={<MoneyIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={formatCurrency(service.defaultPrice)}
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
          {hasCategory && (
            <Chip
              icon={<CategoryIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={service.category}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Box>
      )}
    </Card>
  );
}
